import * as React from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ListIcon from "@mui/icons-material/List";
import Box from "@mui/material/Box";
import { Typography, Button, IconButton, Tooltip } from "@mui/material";
import * as moment from "moment";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import * as calendarFns from "../functions/calendarFunctions";
import * as calendarData from "../functions/calendarData";
import { t, tl } from "../../translate";
import classNames from "../../../helpers/classNames";
import ReportsPanel from "../../../containers/Reports/Panel/ReportsPanel";
import { CalendarSettingsPanel } from "../../../containers/ResourceCentre/ModuleSettings/CalendarSettings/CalendarSettingsPanel";

// @ts-expect-error - css
import styles from "./styles.module.css";
import { convertADtoBS, getAdMonthRangeFromBsMoth } from "../functions/calendarFunctions";

const getMonthYearForTheWeek = (date: Date) => {
  const day1 = moment(date).startOf("week");
  const day7 = moment(date).endOf("week");
  const day1BS = calendarFns.convertADtoBS(day1);
  const day7BS = calendarFns.convertADtoBS(day7);
  const endMonth = ` - ${calendarData.bsMonths(t)[day7BS.bsMonth - 1]} ${day7BS.bsYear}`;
  return `${calendarData.bsMonths(t)[day1BS.bsMonth - 1]} ${day1BS.bsYear}${
    day1BS.bsMonth !== day7BS.bsMonth ? endMonth : ""
  }`;
};

const isWeekend = (day: Date | moment.Moment) => moment(day).day() === 6;

interface CalendarHeaderProps {
  selectedDate: Date;
  days: Array<moment.Moment>;
  goBack: () => void;
  goForward: () => void;
  goto: (date: Date) => void;
  isCalendarView: boolean;
  onCalendarViewChange: () => void;
  columnsMode: string;
  setColumnsMode: (v: string) => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  selectedDate,
  days,
  goBack,
  goForward,
  goto,
  isCalendarView,
  onCalendarViewChange,
  columnsMode,
  setColumnsMode
}): React.JSX.Element => {
  const { bsMonth, bsYear } = convertADtoBS(selectedDate);
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: (theme) => theme.palette.background.paper
      }}
    >
      {/* Calendar Controls */}
      <Box
        sx={{
          height: "40px",
          px: 2,
          py: 3.5,
          display: "flex",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Typography>
            <Box textAlign="center" className={styles.selectedDate} component="span">
              {getMonthYearForTheWeek(selectedDate)} / {getAdMonthRangeFromBsMoth(bsYear, bsMonth)}
            </Box>
          </Typography>
        </Box>

        <Box
          component="span"
          className={styles.headerActionsContainer}
          display="flex"
          alignItems="center"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1
            }}
          >
            <Select
              variant="outlined"
              value={columnsMode}
              onChange={(event) => {
                const cMode = event.target.value;
                setColumnsMode(cMode);
                localStorage.setItem("columnsMode", cMode);
              }}
              size="small"
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
            </Select>
            <Button
              variant="outlined"
              onClick={() => goto(new Date())}
              sx={{
                height: 32,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1
              }}
            >
              {tl("booking.today")}
            </Button>

            <Tooltip title="Previous">
              <IconButton
                data-testmation="leftArrow"
                onClick={goBack}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
            </Tooltip>

            <Tooltip title="Next">
              <IconButton
                data-testmation="rightArrow"
                onClick={goForward}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1
                }}
              >
                <KeyboardArrowRight fontSize="small" color="action" />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={() => onCalendarViewChange()}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1
              }}
            >
              {isCalendarView ? (
                <Tooltip title="Details">
                  <ListIcon color="primary" />
                </Tooltip>
              ) : (
                <Tooltip title="Calendar">
                  <CalendarMonthIcon color="primary" />
                </Tooltip>
              )}
            </IconButton>
            <CalendarSettingsPanel onViewChange={onCalendarViewChange} />
            <ReportsPanel module="calendar" />
          </Box>
        </Box>
      </Box>

      {/* Day headers */}
      <Box sx={{ py: 0.5, display: "flex" }}>
        <div
          className={styles.timeColumnHeader}
          style={{ display: `${!isCalendarView ? "none" : ""}` }}
        />
        {days.map((day) => (
          <Box
            key={day.toISOString()}
            className={classNames(styles.dayHeader, {
              [styles.todayHeader]: moment(day).isSame(moment(), "date"),
              [styles.weekendHeader]: isWeekend(day),
              [styles.dayHeaderListView]: !isCalendarView
            })}
          >
            <Typography sx={{ mb: 1, fontSize: "0.75rem" }}>
              {calendarData.bsDaysFull(t)[day.day()]}
            </Typography>
            <Typography
              style={{ fontWeight: 600, fontSize: "1.2rem" }}
              sx={(theme) => ({
                p: 1,
                display: "inline-block",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                height: "48px",
                ...(moment(day).isSame(moment(), "date")
                  ? {
                      background: theme.palette.primary.main,
                      color: "white"
                    }
                  : {})
              })}
            >
              {calendarFns.convertADtoBS(moment(day)).bsDate}
              <Typography component="span" sx={{ ml: 0.5, fontSize: "10px" }}>
                {moment(day).date()}
              </Typography>
            </Typography>
          </Box>
        ))}
        <div aria-hidden="true" className={styles.calScroll} />
      </Box>
    </Box>
  );
};

export default CalendarHeader;
